<template>
  <div id="app" class="container-fluid">
    <!-- Header with Patient Info -->
    <full_head />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2">Loading weight data...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="alert alert-danger">
        <i class="fas fa-exclamation-triangle"></i>
        {{ error }}
        <button @click="retryFetch" class="btn btn-sm btn-outline-danger ms-3">
          Retry
        </button>
      </div>

      <!-- Content when data is loaded -->
      <div v-else class="weight-monitoring-section">
        <!-- Summary Stats -->
        <div class="summary-stats">
          <div class="stat-card">
            <div class="stat-icon">
              <i class="fas fa-weight-scale"></i>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ currentWeight }} kg</div>
              <div class="stat-label">Current Weight</div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon">
              <i class="fas fa-chart-line"></i>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ totalGain }} kg</div>
              <div class="stat-label">Total Weight Gain</div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon">
              <i class="fas fa-calendar-alt"></i>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ weightRecords.length }}</div>
              <div class="stat-label">Records</div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon">
              <i class="fas fa-baby"></i>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ currentGestation }} wks</div>
              <div class="stat-label">Current Gestation</div>
            </div>
          </div>
        </div>

        <!-- Weight Chart -->
        <div class="card">
          <div class="card-header">
            <h3><i class="fas fa-chart-line"></i> Weight Monitoring Chart</h3>
            <div class="chart-controls">
              <button
                class="btn-chart"
                @click="chartType = 'line'"
                :class="{ active: chartType === 'line' }"
              >
                <i class="fas fa-chart-line"></i> Line
              </button>
              <button
                class="btn-chart"
                @click="chartType = 'bar'"
                :class="{ active: chartType === 'bar' }"
              >
                <i class="fas fa-chart-bar"></i> Bar
              </button>
            </div>
          </div>
          <div class="card-body">
            <div v-if="weightRecords.length === 0" class="no-data-message">
              <i class="fas fa-chart-line fa-3x mb-3" style="color: #ccc"></i>
              <p>
                No weight records available. Add your first record to see the
                chart.
              </p>
            </div>
            <div v-else class="chart-container">
              <canvas ref="weightChart"></canvas>
            </div>
            <div v-if="weightRecords.length > 0" class="chart-legend">
              <div class="legend-item">
                <span
                  class="legend-color"
                  style="background-color: #4a90e2"
                ></span>
                <span>Actual Weight (kg)</span>
              </div>
              <div class="legend-item">
                <span
                  class="legend-color"
                  style="background-color: #50e3c2"
                ></span>
                <span>Expected Weight (kg)</span>
              </div>
              <div class="legend-item">
                <span
                  class="legend-color"
                  style="background-color: #f5a623"
                ></span>
                <span>Recommended Range</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Weight Records Table -->
        <div class="card">
          <div class="card-header">
            <h3><i class="fas fa-table"></i> Weight Records</h3>
            <!-- <button class="btn-add" @click="openAddModal">
              <i class="fas fa-plus"></i> Add Record
            </button> -->
            <b
              >Current Height:
              {{ weightRecords[weightRecords.length - 1]?.height }} cm</b
            >
          </div>
          <div class="card-body">
            <div v-if="weightRecords.length === 0" class="no-data-message">
              <i
                class="fas fa-clipboard-list fa-3x mb-3"
                style="color: #ccc"
              ></i>
              <p>No weight records found. Click "Add Record" to get started.</p>
            </div>
            <div v-else class="table-responsive">
              <table class="table">
                <thead>
                  <tr>
                    <th>Visit Date</th>
                    <th>Gestation (Weeks)</th>
                    <th>Weight (kg)</th>
                    <th>Change (kg)</th>
                    <th>BMI</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="record in weightRecords" :key="record.id">
                    <td>{{ formatDate(record.date) }}</td>
                    <td>{{ record.gestation }} wks</td>
                    <td class="weight-value">{{ record.weight }} kg</td>
                    <td :class="getChangeClass(record.change)">
                      <i v-if="record.change > 0" class="fas fa-arrow-up"></i>
                      <i
                        v-else-if="record.change < 0"
                        class="fas fa-arrow-down"
                      ></i>
                      <i v-else class="fas fa-minus"></i>
                      {{ record.change > 0 ? "+" : "" }}{{ record.change }} kg
                    </td>
                    <td>{{ record.bmi }}</td>
                    <td>{{ record.notes || "-" }}</td>
                    <td class="actions">
                      <button
                        class="btn-action edit"
                        @click="editRecord(record)"
                      >
                        <i class="fas fa-edit"></i>
                      </button>
                      <button
                        class="btn-action delete"
                        @click="deleteRecord(record.id)"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- BMI Guidelines -->
        <div class="card">
          <div class="card-header">
            <h3>
              <i class="fas fa-info-circle"></i> Pregnancy Weight Gain
              Guidelines
            </h3>
          </div>
          <div class="card-body">
            <div class="guidelines-grid">
              <div class="guideline-item">
                <div class="guideline-header underweight">
                  <!-- <h4>Underweight (BMI < 18.5)</h4> -->
                  <h4>Underweight (BMI &lt; 18.5)</h4>
                </div>
                <div class="guideline-content">
                  <div class="recommended-gain">12.5 - 18 kg</div>
                  <div class="rate">0.5 kg/week (2nd & 3rd trimester)</div>
                </div>
              </div>

              <div class="guideline-item">
                <div class="guideline-header normal">
                  <h4>Normal Weight (BMI 18.5-24.9)</h4>
                </div>
                <div class="guideline-content">
                  <div class="recommended-gain">11.5 - 16 kg</div>
                  <div class="rate">0.4 kg/week (2nd & 3rd trimester)</div>
                </div>
              </div>

              <div class="guideline-item">
                <div class="guideline-header overweight">
                  <h4>Overweight (BMI 25-29.9)</h4>
                </div>
                <div class="guideline-content">
                  <div class="recommended-gain">7 - 11.5 kg</div>
                  <div class="rate">0.3 kg/week (2nd & 3rd trimester)</div>
                </div>
              </div>

              <div class="guideline-item">
                <div class="guideline-header obese">
                  <h4>Obese (BMI ≥ 30)</h4>
                </div>
                <div class="guideline-content">
                  <div class="recommended-gain">5 - 9 kg</div>
                  <div class="rate">0.2 kg/week (2nd & 3rd trimester)</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Record Modal -->
    <div
      v-if="showAddRecordModal"
      class="modal-overlay"
      @click.self="closeModal"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingRecord ? "Edit" : "Add" }} Weight Record</h3>
          <button class="modal-close" @click="closeModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveRecord">
            <div class="form-grid">
              <div class="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  v-model="form.date"
                  required
                  class="form-control"
                  :max="todayDate"
                />
              </div>

              <div class="form-group">
                <label>Gestation (weeks) *</label>
                <input
                  type="number"
                  v-model.number="form.gestation"
                  min="4"
                  max="42"
                  required
                  class="form-control"
                />
              </div>

              <div class="form-group">
                <label>Weight (kg) *</label>
                <input
                  type="number"
                  v-model.number="form.weight"
                  min="30"
                  max="150"
                  step="0.1"
                  required
                  class="form-control"
                />
              </div>

              <div class="form-group">
                <label>Height (cm) *</label>
                <input
                  type="number"
                  v-model.number="form.height"
                  min="120"
                  max="220"
                  required
                  class="form-control"
                />
              </div>

              <div class="form-group full-width">
                <label>Notes</label>
                <textarea
                  v-model="form.notes"
                  class="form-control"
                  rows="3"
                  placeholder="Add any relevant notes..."
                ></textarea>
              </div>
            </div>

            <div class="form-preview">
              <div class="preview-item">
                <span>BMI:</span>
                <strong>{{ calculatedBMI }}</strong>
              </div>
              <div class="preview-item">
                <span>Category:</span>
                <strong>{{ bmiCategory }}</strong>
              </div>
            </div>

            <div class="modal-actions">
              <button
                type="button"
                class="btn btn-secondary"
                @click="closeModal"
              >
                Cancel
              </button>
              <button type="submit" class="btn btn-primary">
                <i class="fas fa-save"></i>
                {{ editingRecord ? "Update" : "Save" }} Record
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import full_head from "./full_head.vue";
import { Chart, registerables } from "chart.js";
import { generalApiRequest } from "../../../../api/apiIndex";

Chart.register(...registerables);

export default {
  name: "ANCWeightMonitoring",
  components: {
    full_head,
  },
  data() {
    return {
      loading: true,
      error: null,
      chartType: "line",
      weightChart: null,
      showAddRecordModal: false,
      editingRecord: null,
      form: {
        date: new Date().toISOString().split("T")[0],
        gestation: null,
        weight: null,
        height: null,
        notes: "",
      },
      currentGestation: 0,
      currentWeight: 0,
      totalGain: 0,
      weightRecords: [],
    };
  },
  computed: {
    todayDate() {
      return new Date().toISOString().split("T")[0];
    },
    calculatedBMI() {
      if (!this.form.weight || !this.form.height) return "0.0";
      const heightM = this.form.height / 100;
      return (this.form.weight / (heightM * heightM)).toFixed(1);
    },
    bmiCategory() {
      const bmi = parseFloat(this.calculatedBMI);
      if (bmi === 0) return "N/A";
      if (bmi < 18.5) return "Underweight";
      if (bmi < 25) return "Normal";
      if (bmi < 30) return "Overweight";
      return "Obese";
    },
  },
  async mounted() {
    await this.fetchPregnancyData();
    this.renderChart();
  },
  methods: {
    async fetchPregnancyData() {
      this.loading = true;
      this.error = null;

      try {
        const response = await generalApiRequest(
          `anc_pregnancy_data/visit/${this.$route.params.visit_id}`,
          "GET",
          {},
          1,
        );

        if (response.data && Array.isArray(response.data)) {
          if (response.data.length > 0) {
            this.weightRecords = response.data
              .map((record) => ({
                id: record.id,
                date: record.visit_date,
                gestation: record.gestation || 0,
                weight: parseFloat(record.weight),
                height: parseFloat(record.height),
                change: 0,
                bmi: record.bmi
                  ? parseFloat(record.bmi).toFixed(1)
                  : this.calculateBMIValue(record.weight, record.height),
                notes: record.notes || "",
              }))
              .sort((a, b) => new Date(a.date) - new Date(b.date));

            this.calculateChanges();
            this.updateCurrentStats();

            // Render chart after data is loaded
            this.$nextTick(() => {
              this.renderChart();
            });
          } else {
            this.weightRecords = [];
          }
        } else {
          this.weightRecords = [];
        }
      } catch (error) {
        console.error("Error fetching pregnancy data:", error);
        this.error = "Failed to load weight data. Please try again.";
        this.weightRecords = [];
      } finally {
        this.loading = false;
      }
    },

    retryFetch() {
      this.fetchPregnancyData();
    },

    calculateBMIValue(weight, height) {
      if (!weight || !height) return "0.0";
      const heightM = height / 100;
      return (weight / (heightM * heightM)).toFixed(1);
    },

    calculateChanges() {
      if (this.weightRecords.length === 0) return;

      this.weightRecords[0].change = 0;

      for (let i = 1; i < this.weightRecords.length; i++) {
        this.weightRecords[i].change = parseFloat(
          (
            this.weightRecords[i].weight - this.weightRecords[i - 1].weight
          ).toFixed(1),
        );
      }
    },

    updateCurrentStats() {
      if (this.weightRecords.length === 0) {
        this.currentWeight = 0;
        this.totalGain = 0;
        this.currentGestation = 0;
        return;
      }

      const lastRecord = this.weightRecords[this.weightRecords.length - 1];
      const firstRecord = this.weightRecords[0];

      this.currentWeight = lastRecord.weight;
      this.totalGain = parseFloat(
        (lastRecord.weight - firstRecord.weight).toFixed(1),
      );
      this.currentGestation = lastRecord.gestation;
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    getChangeClass(change) {
      if (change > 2) return "change-high";
      if (change > 0.5) return "change-normal";
      if (change < 0) return "change-low";
      return "change-stable";
    },

    openAddModal() {
      this.editingRecord = null;
      this.resetForm();
      this.showAddRecordModal = true;
    },

    editRecord(record) {
      this.editingRecord = record;
      this.form = {
        date: record.date,
        gestation: record.gestation,
        weight: record.weight,
        height: record.height,
        notes: record.notes || "",
      };
      this.showAddRecordModal = true;
    },

    async deleteRecord(id) {
      if (!confirm("Are you sure you want to delete this record?")) return;

      try {
        // Uncomment and implement API delete call if needed
        // await generalApiRequest(`anc_pregnancy_data/${id}`, "DELETE", {}, 1);

        this.weightRecords = this.weightRecords.filter(
          (record) => record.id !== id,
        );

        this.calculateChanges();
        this.updateCurrentStats();
        this.renderChart();
      } catch (error) {
        console.error("Error deleting record:", error);
        alert("Failed to delete record. Please try again.");
      }
    },

    closeModal() {
      this.showAddRecordModal = false;
      this.editingRecord = null;
    },

    resetForm() {
      const lastRecord =
        this.weightRecords.length > 0
          ? this.weightRecords[this.weightRecords.length - 1]
          : null;

      this.form = {
        date: new Date().toISOString().split("T")[0],
        gestation: lastRecord ? lastRecord.gestation + 4 : 12,
        weight: lastRecord ? lastRecord.weight : 60,
        height: lastRecord ? lastRecord.height : 165,
        notes: "",
      };
    },

    async saveRecord() {
      const bmi = this.calculatedBMI;

      try {
        // Uncomment and implement API save/update call if needed
        // const payload = {
        //   visit_date: this.form.date,
        //   gestation: this.form.gestation,
        //   weight: this.form.weight,
        //   height: this.form.height,
        //   bmi: parseFloat(bmi),
        //   notes: this.form.notes,
        // };

        // if (this.editingRecord) {
        //   await generalApiRequest(`anc_pregnancy_data/${this.editingRecord.id}`, "PUT", payload, 1);
        // } else {
        //   await generalApiRequest('anc_pregnancy_data', "POST", payload, 1);
        // }

        if (this.editingRecord) {
          const index = this.weightRecords.findIndex(
            (r) => r.id === this.editingRecord.id,
          );

          if (index !== -1) {
            this.weightRecords[index] = {
              ...this.weightRecords[index],
              date: this.form.date,
              gestation: this.form.gestation,
              weight: this.form.weight,
              height: this.form.height,
              bmi: bmi,
              notes: this.form.notes,
            };
          }
        } else {
          const newRecord = {
            id: Date.now(),
            date: this.form.date,
            gestation: this.form.gestation,
            weight: this.form.weight,
            height: this.form.height,
            change: 0,
            bmi: bmi,
            notes: this.form.notes,
          };
          this.weightRecords.push(newRecord);
        }

        // Sort by date
        this.weightRecords.sort((a, b) => new Date(a.date) - new Date(b.date));

        this.calculateChanges();
        this.updateCurrentStats();
        this.renderChart();
        this.closeModal();
      } catch (error) {
        console.error("Error saving record:", error);
        alert("Failed to save record. Please try again.");
      }
    },

    renderChart() {
      // Destroy existing chart if it exists
      if (this.weightChart) {
        this.weightChart.destroy();
        this.weightChart = null;
      }

      // Don't render if no records or no canvas element
      if (!this.weightRecords || this.weightRecords.length === 0) return;
      if (!this.$refs.weightChart) return;

      const ctx = this.$refs.weightChart.getContext("2d");
      if (!ctx) return;

      const gestationWeeks = this.weightRecords.map((r) => r.gestation);
      const actualWeights = this.weightRecords.map((r) => r.weight);
      const initialWeight = this.weightRecords[0].weight;
      const initialBMI = parseFloat(this.weightRecords[0].bmi);
      const initialGestation = this.weightRecords[0].gestation;

      // Calculate expected weight based on BMI category
      const expectedWeights = gestationWeeks.map((week) => {
        const weeksFromStart = week - initialGestation;
        let weeklyGain = 0.4; // Default normal weight

        if (initialBMI < 18.5) {
          weeklyGain = 0.5;
        } else if (initialBMI >= 25 && initialBMI < 30) {
          weeklyGain = 0.3;
        } else if (initialBMI >= 30) {
          weeklyGain = 0.2;
        }

        return parseFloat(
          (initialWeight + weeklyGain * Math.max(0, weeksFromStart)).toFixed(1),
        );
      });

      // Calculate recommended range
      const upperRange = expectedWeights.map((w) =>
        parseFloat((w + 2).toFixed(1)),
      );
      const lowerRange = expectedWeights.map((w) =>
        parseFloat((w - 2).toFixed(1)),
      );

      const yMin = Math.floor(Math.min(...actualWeights, ...lowerRange) - 5);
      const yMax = Math.ceil(Math.max(...actualWeights, ...upperRange) + 5);

      this.weightChart = new Chart(ctx, {
        type: this.chartType,
        data: {
          labels: gestationWeeks.map((w) => `${w} wks`),
          datasets: [
            {
              label: "Actual Weight",
              data: actualWeights,
              borderColor: "#4A90E2",
              backgroundColor:
                this.chartType === "bar"
                  ? "rgba(74, 144, 226, 0.7)"
                  : "transparent",
              borderWidth: 3,
              pointRadius: 5,
              pointBackgroundColor: "#4A90E2",
              fill: false,
              tension: 0.3,
            },
            {
              label: "Expected Weight",
              data: expectedWeights,
              borderColor: "#50E3C2",
              backgroundColor:
                this.chartType === "bar"
                  ? "rgba(80, 227, 194, 0.7)"
                  : "transparent",
              borderWidth: 2,
              borderDash: [5, 5],
              pointRadius: 3,
              fill: false,
              tension: 0.3,
            },
            {
              label: "Upper Range",
              data: upperRange,
              borderColor: "rgba(245, 166, 35, 0.3)",
              backgroundColor: "rgba(245, 166, 35, 0.1)",
              borderWidth: 1,
              borderDash: [2, 2],
              pointRadius: 0,
              fill: false,
              tension: 0.3,
            },
            {
              label: "Lower Range",
              data: lowerRange,
              borderColor: "rgba(245, 166, 35, 0.3)",
              backgroundColor: "rgba(245, 166, 35, 0.1)",
              borderWidth: 1,
              borderDash: [2, 2],
              pointRadius: 0,
              fill: {
                target: 2, // Fill to the "Upper Range" dataset
                above: "rgba(245, 166, 35, 0.1)",
              },
              tension: 0.3,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            intersect: false,
            mode: "index",
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  if (context.dataset.label.includes("Range")) return null;
                  return `${context.dataset.label}: ${context.raw} kg`;
                },
              },
            },
          },
          scales: {
            x: {
              title: {
                display: true,
                text: "Gestation (Weeks)",
                font: {
                  weight: "bold",
                },
              },
              grid: {
                display: false,
              },
            },
            y: {
              title: {
                display: true,
                text: "Weight (kg)",
                font: {
                  weight: "bold",
                },
              },
              min: yMin,
              max: yMax,
              ticks: {
                stepSize: 1,
              },
            },
          },
        },
      });
    },
  },
  watch: {
    chartType() {
      this.renderChart();
    },
  },
};
</script>

<style scoped>
.container-fluid {
  padding: 20px;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.main-content {
  margin-top: 20px;
}

/* Loading and Error States */
.loading-container {
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
}

.loading-container .spinner-border {
  width: 3rem;
  height: 3rem;
}

.alert {
  padding: 15px 20px;
  border-radius: 10px;
  margin-bottom: 25px;
}

.no-data-message {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
}

.no-data-message p {
  font-size: 16px;
  margin-top: 10px;
}

/* Summary Stats */
.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 25px;
}

.stat-card {
  background: white;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.stat-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 20px;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 5px;
}

.stat-label {
  color: #7f8c8d;
  font-size: 14px;
  font-weight: 600;
}

/* Cards */
.card {
  background: white;
  border-radius: 10px;
  margin-bottom: 25px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  border: none;
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 20px;
  border-bottom: 2px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.card-header h3 {
  margin: 0;
  color: #495057;
  font-size: 20px;
  font-weight: 600;
}

.card-header h3 i {
  margin-right: 10px;
  color: #667eea;
}

.chart-controls {
  display: flex;
  gap: 10px;
}

.btn-chart {
  padding: 8px 15px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  transition: all 0.3s;
}

.btn-chart:hover {
  border-color: #667eea;
}

.btn-chart.active {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.btn-add {
  padding: 8px 20px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 600;
  transition: background 0.3s;
}

.btn-add:hover {
  background: #218838;
}

.card-body {
  padding: 25px;
}

/* Chart */
.chart-container {
  height: 400px;
  position: relative;
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.legend-color {
  width: 20px;
  height: 5px;
  border-radius: 2px;
}

/* Table */
.table-responsive {
  overflow-x: auto;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th {
  background: #f8f9fa;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
}

.table td {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
}

.table tbody tr:hover {
  background-color: #f8f9fa;
}

.weight-value {
  font-weight: 600;
  color: #2c3e50;
}

.change-high {
  color: #e74c3c;
  font-weight: 600;
}

.change-normal {
  color: #27ae60;
  font-weight: 600;
}

.change-low {
  color: #f39c12;
  font-weight: 600;
}

.change-stable {
  color: #7f8c8d;
  font-weight: 600;
}

.actions {
  display: flex;
  gap: 10px;
}

.btn-action {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s;
}

.btn-action:hover {
  opacity: 0.8;
}

.btn-action.edit {
  background: #3498db;
  color: white;
}

.btn-action.delete {
  background: #e74c3c;
  color: white;
}

/* Guidelines */
.guidelines-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.guideline-item {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
}

.guideline-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.guideline-header {
  padding: 15px;
  color: white;
  text-align: center;
}

.guideline-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.guideline-header.underweight {
  background: #3498db;
}

.guideline-header.normal {
  background: #27ae60;
}

.guideline-header.overweight {
  background: #f39c12;
}

.guideline-header.obese {
  background: #e74c3c;
}

.guideline-content {
  padding: 20px 15px;
  background: white;
  text-align: center;
}

.recommended-gain {
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 10px;
}

.rate {
  color: #7f8c8d;
  font-size: 14px;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  background: white;
  border-radius: 10px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
}

.modal-close {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 5px;
  transition: opacity 0.3s;
}

.modal-close:hover {
  opacity: 0.8;
}

.modal-body {
  padding: 30px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 5px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  color: #495057;
}

.form-control {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-preview {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 5px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-around;
}

.preview-item {
  text-align: center;
}

.preview-item span {
  display: block;
  color: #7f8c8d;
  font-size: 14px;
  margin-bottom: 5px;
}

.preview-item strong {
  font-size: 18px;
  color: #2c3e50;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
}

.btn {
  padding: 10px 25px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5568d3;
}

/* Responsive */
@media (max-width: 768px) {
  .summary-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .guidelines-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 576px) {
  .container-fluid {
    padding: 10px;
  }

  .summary-stats {
    grid-template-columns: 1fr;
  }

  .card-header {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }

  .chart-legend {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .chart-container {
    height: 300px;
  }

  .guidelines-grid {
    grid-template-columns: 1fr;
  }

  .form-preview {
    flex-direction: column;
    gap: 10px;
  }
}
</style>
